import type { CountryDto } from '@api/WhisApi/generated/model/countryDto';
import type { UserDto } from '@api/WhisApi/generated/model/userDto';
import type { SupportedLanguage } from '@store/i18n';
import { supportedLanguages } from '@store/i18n';

const buildLanguagePriority = (
  language: SupportedLanguage,
): SupportedLanguage[] => [
  language,
  ...supportedLanguages.filter((lang) => lang !== language),
];

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

export const getLocalizedLabel = (
  names: Record<string, string> | undefined,
  language: SupportedLanguage,
): string => {
  if (!names) {
    return '';
  }

  const languagePriority = buildLanguagePriority(language);
  const prioritizedValue = languagePriority
    .map((lang) => names[lang])
    .find(isNonEmptyString);

  if (prioritizedValue) {
    return prioritizedValue;
  }

  const fallbackValue = Object.values(names).find(isNonEmptyString);

  return fallbackValue ?? '';
};

export const getUserFullName = (user: UserDto): string => {
  const fullName = [user.firstName, user.lastName]
    .filter((value) => Boolean(value && value.trim().length > 0))
    .join(' ')
    .trim();

  return fullName.length > 0 ? fullName : user.email ?? '';
};

export const getCountryLabel = (
  country: CountryDto | undefined,
  language: SupportedLanguage,
): string => {
  if (!country) {
    return '';
  }

  return getLocalizedLabel(country.name, language) || country.code;
};
